import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Button, Table, Modal, Form } from "react-bootstrap";
import { FaEye, FaTrash } from "react-icons/fa";

const ITEMS_PER_PAGE = 10;

const ExcelReader = ({
  file,
  onResetFile,
  headers,
  setHeaders,
  setSelectedContacts,
}) => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
        type: "array",
      });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: true,
      });

      if (jsonData.length > 1) {
        const extractedHeaders = jsonData[0];
        const body = jsonData.slice(1);

        const parseDate = (value) => {
          if (!value) return null;
          if (typeof value === "number") {
            const parsed = XLSX.SSF.parse_date_code(value);
            if (parsed) {
              const year = parsed.y < 100 ? parsed.y + 1900 : parsed.y;
              const month = String(parsed.m).padStart(2, "0");
              const day = String(parsed.d).padStart(2, "0");
              return `${year}-${month}-${day}`;
            }
          }
          return value;
        };

        const extractedData = body.map((row) => {
          let obj = {};
          extractedHeaders.forEach((key, index) => {
            obj[key] =
              key.toLowerCase().includes("tanggal") ||
              key.toLowerCase().includes("waktu")
                ? parseDate(row[index])
                : row[index] || null;
          });
          return obj;
        });

        setHeaders(extractedHeaders);
        setSelectedContacts(extractedData);
        setData(extractedData);
      }
    };

    reader.readAsArrayBuffer(file);
  }, [file]);

  const handleDelete = () => {
    setHeaders([]);
    setSelectedContacts([]);
    setData([]);
    onResetFile();
    setShowModal(false);
  };

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const paginatedData = data.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const filteredData = paginatedData.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div>
      {data.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-custome w-100 mb-0">
            <thead>
              <tr className="text-white">
                <th className="text-center p-2" style={{ width: "15%" }}>
                  Total Data
                </th>
                <th className="text-center p-2">Fields</th>
                <th className="text-center p-2" style={{ width: "13%" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center p-2">{data.length} data</td>
                <td className="text-start p-2">
                  {headers.map((header, index) => (
                    <span key={index} className="badge bg-primary me-1">
                      {header}
                    </span>
                  ))}
                </td>
                <td className="text-center p-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setCurrentPage(1);
                      setShowModal(true);
                    }}
                  >
                    <FaEye size={14} />
                  </Button>{" "}
                  <Button variant="danger" size="sm" onClick={handleDelete}>
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Modal untuk menampilkan tabel detail dengan pagination */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size={
          headers.length > 5 && headers.length < 10
            ? "lg"
            : headers.length >= 10
            ? "xl"
            : "lg"
        }
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Show Recipient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Search Bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div></div> {/* Kosong untuk menjaga posisi */}
            <Form.Control
              type="text"
              placeholder="Search..."
              className="w-25"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {/* Tabel Data */}
          <div className="table-responsive">
            <Table
              className="table table-striped table-bordered table-custome"
              bordered
            >
              <thead>
                <tr
                  className="text-white"
                  style={{ backgroundColor: "#13213c" }}
                >
                  {headers.map((header, index) => (
                    <th key={index} className="text-center p-2">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, rowIndex) => (
                  <tr key={rowIndex}>
                    {headers.map((header, colIndex) => (
                      <td
                        key={colIndex}
                        className={
                          typeof item[header] === "number"
                            ? "text-end p-2"
                            : "text-start p-2"
                        }
                      >
                        {item[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls (Next & Prev) */}
          <div className="d-flex justify-content-between mt-3">
            <div>
              Page {currentPage} of {totalPages}
            </div>
            {totalPages > 1 && (
              <div>
                <Button
                  variant="primary"
                  className="btn btn-sm me-2"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Prev
                </Button>
                <Button
                  variant="primary"
                  className="btn btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ExcelReader;
